import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { getSrc } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { isMobile } from 'react-device-detect'

// Components
import AnimateScaleIn from '../animation/AnimateScaleIn'
import AnimateSlideText from '../animation/AnimateSlideText'
import ContainedWrap from '../ContainedWrap'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'
import SummaryStat from '../SummaryStat'

// Styles
import {
  SummaryMain,
  SummaryBackgroundWrap,
  SummaryBackground,
  SummaryStats,
  SummaryCopy,
  SummaryExtraCopy,
} from './index.style'
import { colors } from '../../styles/vars/colors.style'
import Button from '../Button'

const Summary = ({
  id,
  heading,
  text,
  button,
  additionalText,
  image,
  stat1,
  stat1Image,
  stat2,
  stat2Image,
  stat3,
  stat3Image,
  linkId,
}) => {
  const stat2SplitIndex = stat2.lastIndexOf(' ')
  const stat2Start = stat2.substring(0, stat2SplitIndex)
  const stat2End = stat2.substring(stat2SplitIndex + 1, stat2.length)

  const imageSource = getSrc(image.gatsbyImageData)

  const { allContentfulGlobalSettings } = useStaticQuery(graphql`
    query {
      allContentfulGlobalSettings(limit: 1) {
        nodes {
          reservationContactNumber
        }
      }
    }
  `)

  const { reservationContactNumber } = allContentfulGlobalSettings.nodes[0]

  return (
    <ScrollSection>
      <>
        <SummaryBackgroundWrap>
          <AnimateScaleIn id={id} delay={0}>
            <SummaryBackground bg={imageSource} />
          </AnimateScaleIn>
        </SummaryBackgroundWrap>
        <SummaryMain id={linkId && linkId}>
          <Spacer size={[63, 250]} />
          <Container>
            <Grid>
              <GridItem
                tiny={12}
                tinyAlign={`center`}
                tabletP={10}
                tabletPStart={2}
                tabletL={5}
                desk={3}
                deskStart={2}
              >
                <ContainedWrap size={438} center={false}>
                  <RichText
                    id={`${id}-heading`}
                    content={heading}
                    forceHeading
                    delayEm={true}
                  />
                  <Spacer size={30} />
                  <SummaryCopy>
                    <RichText id={`${id}-text`} content={text} />
                  </SummaryCopy>
                  {button && (
                    <>
                      <Spacer size={[30, 52]} />
                      <AnimateSlideText id={`${id}-cta`} duration={0.6}>
                        <Button
                          href={
                            isMobile
                              ? `tel:${reservationContactNumber}`
                              : button.externalLink
                          }
                          variant="outline"
                          color={colors.light}
                          target={isMobile ? undefined : `_blank`}
                        >
                          {button.label}
                        </Button>
                      </AnimateSlideText>
                    </>
                  )}
                  {additionalText && (
                    <SummaryExtraCopy>
                      <Spacer size={[10, 20]} />
                      <RichText
                        id={`${id}-additional`}
                        content={additionalText}
                      />
                    </SummaryExtraCopy>
                  )}
                </ContainedWrap>
              </GridItem>
              <GridItem
                tiny={12}
                tinyAlign={`flex-start`}
                tabletP={10}
                tabletPStart={2}
                tabletL={6}
                tabletLStart={7}
                desk={8}
                deskStart={5}
              >
                <SummaryStats>
                  {stat1 && (
                    <SummaryStat
                      id={id + '-stat-1'}
                      inView={true}
                      delay={2}
                      text1={stat1}
                      images={stat1Image}
                      imgOrder={1}
                    />
                  )}
                  {stat2 && (
                    <SummaryStat
                      id={id + '-stat-2'}
                      inView={true}
                      delay={2.25}
                      text1={stat2Start}
                      text2={stat2End}
                      images={stat2Image}
                      imgOrder={3}
                    />
                  )}
                  {stat3 && (
                    <SummaryStat
                      id={id + '-stat-3'}
                      inView={true}
                      delay={2.5}
                      text1={stat3}
                      images={stat3Image}
                      imgOrder={5}
                    />
                  )}
                </SummaryStats>
              </GridItem>
            </Grid>
          </Container>
          <Spacer size={[63, 250]} />
        </SummaryMain>
      </>
    </ScrollSection>
  )
}

Summary.propTypes = {
  id: PropTypes.string.isRequired,
  heading: PropTypes.object.isRequired,
  text: PropTypes.object,
  image: PropTypes.object.isRequired,
  link: PropTypes.string,
  button: PropTypes.object,
  extraText: PropTypes.string,
  stat1: PropTypes.string,
  stat1Image: PropTypes.array,
  stat2: PropTypes.string,
  stat2Image: PropTypes.array,
  stat3: PropTypes.string,
  stat3Image: PropTypes.array,
}

export default Summary
